/*  GuidedCallbackForm — reusable lead-capture form for the four
    Guards & Retainer product pages.
    ────────────────────────────────────────────────────────────────
    Previously this form was inlined on each product page (Secure,
    Serene, and now Apex). Duplicated ~200 LOC. Extracted here so:
      • copy / layout tweaks land in one place,
      • each page can preselect the interest that matches its product,
      • pages that want extra qualifiers (primary concern, sport,
        competitive level) pass them via `extraSelects` and their
        selected value is folded into the notes payload — no schema
        change to `/api/product-enquiry`.

    The section renders with `id="guided-callback-anchor"` so the
    site-header mega-menu footer CTA ("Talk to a smile expert") can
    deep-link straight to the form on the Night-Guard page.
*/
import React, { useState } from "react";
import { toast } from "sonner";
import {
    Phone, MapPin, CheckCircle,
    User as UserIcon, Envelope, PaperPlaneRight, ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";


const DEFAULT_INTEREST_OPTIONS = [
    { slug: "night-guard", label: "Night Guard" },
    { slug: "anti-snoring", label: "Anti-Snoring Guard" },
    { slug: "sports-guard", label: "Sports Guard" },
    { slug: "retainer", label: "Retainer" },
];


// Theme tint presets so each page can pass one word instead of a
// long tailwind gradient — keeps call sites clean.
const THEMES = {
    indigo: "bg-gradient-to-br from-indigo-50 via-white to-primary/[0.06]",
    sky:    "bg-gradient-to-br from-sky-50 via-white to-primary/[0.06]",
    amber:  "bg-gradient-to-br from-amber-50 via-white to-primary/[0.06]",
};


/**
 * @param {object}   props
 * @param {string}   props.productSlug     — primary product for the lead source.
 * @param {string}   props.defaultInterest — pre-checked interest pill (product slug).
 * @param {string}  [props.themeTint]      — "indigo" | "sky" | "amber".
 * @param {string}   props.testPrefix      — data-testid namespace (secure/serene/apex).
 * @param {string}  [props.eyebrow]        — small overline copy.
 * @param {string}  [props.headline]       — section h2.
 * @param {string}  [props.blurb]          — section sub-copy.
 * @param {string}  [props.submitLabel]    — button label.
 * @param {Array}   [props.extraSelects]   — optional single-select pill rows;
 *      shape: [{ key, label, options: string[] }]. Selected values are
 *      prepended to `notes` when submitting so ops sees them in the Leads Hub.
 * @param {Array}   [props.interestOptions] — override the "I'm interested in"
 *      pill palette. Defaults to the 4 Guards & Retainer products. The
 *      neutral `/contact` landing passes a 5-item list that adds `aligners`.
 */
export default function GuidedCallbackForm({
    productSlug,
    defaultInterest,
    themeTint = "indigo",
    testPrefix,
    eyebrow = "Guided callback",
    headline = "Not Sure Where to Start? We'll Help You.",
    blurb = "Leave your details and an Invisique specialist will reach out to guide you to the nearest certified provider.",
    submitLabel = "Request My Free Callback",
    extraSelects = [],
    interestOptions = DEFAULT_INTEREST_OPTIONS,
}) {
    const [form, setForm] = useState({
        name: "", phone: "", email: "", city: "", notes: "", consent_given: true,
    });
    const [interested, setInterested] = useState({ [defaultInterest]: true });
    // Values for optional single-select pill rows, keyed by extraSelect.key.
    const [extras, setExtras] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const toggleInterest = (slug) => {
        setInterested((s) => ({ ...s, [slug]: !s[slug] }));
    };
    const setExtra = (key, val) => {
        setExtras((s) => ({ ...s, [key]: s[key] === val ? "" : val }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const phone = form.phone.replace(/\D/g, "");
        if (phone.length < 6) { toast.error("Please enter a valid phone number."); return; }
        if (!form.consent_given) { toast.error("Please agree to be contacted."); return; }
        const interested_in = Object.entries(interested).filter(([, v]) => v).map(([k]) => k);
        if (interested_in.length === 0) { toast.error("Please pick at least one product."); return; }

        // Fold any selected extras into the notes field with a labelled
        // prefix so the Leads Hub shows them without a schema change.
        const extraLines = extraSelects
            .map((cfg) => (extras[cfg.key] ? `${cfg.label}: ${extras[cfg.key]}` : null))
            .filter(Boolean);
        const notes = [...extraLines, form.notes].filter(Boolean).join("\n");

        setSubmitting(true);
        try {
            await api.post("/product-enquiry", {
                ...form,
                notes,
                phone,
                product_slug: productSlug,
                interested_in,
            });
            setDone(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
        } finally { setSubmitting(false); }
    };

    return (
        <section
            id="guided-callback-anchor"
            className={`${THEMES[themeTint] || THEMES.indigo} border-y border-border`}
            data-testid={`${testPrefix}-lead-form-section`}
        >
            <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
                <div className="text-center">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{eyebrow}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-lead-form-headline`}>
                        {headline}
                    </h2>
                    <p className="mt-3 text-sm text-muted-foreground max-w-xl mx-auto">
                        {blurb}
                    </p>
                </div>

                {done ? (
                    <div className="mt-8 bg-card border border-emerald-200 rounded-2xl p-8 text-center" data-testid={`${testPrefix}-lead-form-done`}>
                        <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3">
                            <CheckCircle size={28} weight="fill" className="text-emerald-600" />
                        </div>
                        <div className="font-display text-2xl font-bold text-foreground">Callback booked.</div>
                        <div className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                            An Invisique specialist will call you shortly to guide you to a certified provider near you.
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 bg-card border border-border rounded-3xl p-6 sm:p-8 shadow-lg" data-testid={`${testPrefix}-lead-form`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <FieldLabel label="Full Name">
                                <div className="relative">
                                    <UserIcon size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Your name"
                                        maxLength={80}
                                        className="pl-8"
                                        data-testid={`${testPrefix}-lead-name`}
                                    />
                                </div>
                            </FieldLabel>
                            <FieldLabel label="Phone Number *">
                                <div className="relative">
                                    <Phone size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        placeholder="Mobile number"
                                        type="tel"
                                        maxLength={20}
                                        required
                                        className="pl-8"
                                        data-testid={`${testPrefix}-lead-phone`}
                                    />
                                </div>
                            </FieldLabel>
                            <FieldLabel label="Email Address">
                                <div className="relative">
                                    <Envelope size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="you@example.com"
                                        type="email"
                                        maxLength={120}
                                        className="pl-8"
                                        data-testid={`${testPrefix}-lead-email`}
                                    />
                                </div>
                            </FieldLabel>
                            <FieldLabel label="Your City">
                                <div className="relative">
                                    <MapPin size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={form.city}
                                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                                        placeholder="City"
                                        maxLength={60}
                                        className="pl-8"
                                        data-testid={`${testPrefix}-lead-city`}
                                    />
                                </div>
                            </FieldLabel>
                        </div>

                        {extraSelects.map((cfg) => (
                            <div className="mt-5" key={cfg.key}>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                                    {cfg.label}
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-lead-${cfg.key}`}>
                                    {cfg.options.map((opt) => {
                                        const on = extras[cfg.key] === opt;
                                        return (
                                            <button
                                                key={opt}
                                                type="button"
                                                onClick={() => setExtra(cfg.key, opt)}
                                                data-testid={`${testPrefix}-lead-${cfg.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-[12px] font-semibold transition-all border ${on ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-white text-foreground border-border hover:border-primary/40"}`}
                                            >
                                                {opt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        <div className="mt-5">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">I&apos;m interested in</div>
                            <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-lead-interest-pills`}>
                                {interestOptions.map((opt) => {
                                    const on = !!interested[opt.slug];
                                    return (
                                        <label
                                            key={opt.slug}
                                            data-testid={`${testPrefix}-lead-interest-${opt.slug}`}
                                            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all border ${
                                                on
                                                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                                    : "bg-white text-foreground border-border hover:border-primary/40"
                                            }`}
                                        >
                                            <Checkbox
                                                checked={on}
                                                onCheckedChange={() => toggleInterest(opt.slug)}
                                                className={on ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-primary" : ""}
                                            />
                                            {opt.label}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-5">
                            <FieldLabel label="Anything you'd like to share? (optional)">
                                <Textarea
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    placeholder="Symptoms, insurance questions, timing…"
                                    rows={3}
                                    maxLength={500}
                                    data-testid={`${testPrefix}-lead-notes`}
                                />
                            </FieldLabel>
                        </div>

                        <label className="mt-5 flex items-start gap-2 text-[11.5px] text-muted-foreground cursor-pointer">
                            <Checkbox
                                checked={form.consent_given}
                                onCheckedChange={(v) => setForm({ ...form, consent_given: !!v })}
                                data-testid={`${testPrefix}-lead-consent`}
                            />
                            <span>I agree to be contacted about my enquiry. No spam, ever — opt out anytime.</span>
                        </label>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full mt-5 h-12 text-sm font-semibold"
                            data-testid={`${testPrefix}-lead-submit`}
                        >
                            <PaperPlaneRight size={14} weight="fill" className="mr-2" />
                            {submitting ? "Sending…" : submitLabel}
                        </Button>

                        <div className="mt-4 flex items-center gap-1.5 text-[10.5px] text-muted-foreground justify-center">
                            <ShieldCheck size={11} weight="duotone" className="text-emerald-600" />
                            <span>Your information is private & never shared.</span>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
}


function FieldLabel({ label, children }) {
    return (
        <label className="block space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
            {children}
        </label>
    );
}
